import { ORDER_TYPES } from '@/const/orders.const';
import { OrderPaymentStatus, OrderStatus, OrderType } from '@/enums/OrderEnums';
import { ICall } from '@/interfaces/ICall';
import { IOrder } from '@/interfaces/IOrder';
import { isNewOrder, isPendingOrder } from '@/utils/orders.utils';
import { defineStore } from 'pinia';
import { usePrintedStore } from '@/stores/printed.store';
import { computed, reactive, toRefs } from 'vue';

export const useOrdersStore = defineStore('orders', () => {
  const state = reactive({
    orders: null as IOrder[] | null,
    // Client-clock stamp of the last full orders snapshot; null until one
    // loads (WS events alone leave a partial array).
    snapshotLoadedAt: null as number | null,
    unseenOrderIds: [] as number[],
    calls: null as ICall[] | null,
    // Device-local (never persisted/synced) ids of calls the user acknowledged
    // from the app-bar incoming alert; silences the call sound without
    // confirming the calls. A new call is never in this list, so the sound
    // re-arms automatically.
    acknowledgedCallIds: [] as number[],
  });

  // Client-clock receipt stamp per order id. Lets updateOrders' remove pass
  // keep orders that arrived (via WS) AFTER a full-reload snapshot was
  // requested — the snapshot's absence is stale, not a deletion. Client-only
  // timestamps on both sides, so server/client clock skew can't break it.
  const orderReceivedAt = new Map<number, number>();

  const getters = {
    newOrders: computed(() => {
      if (!state.orders) return null;
      return state.orders.filter(isNewOrder);
    }),
    pendingOrders: computed(() => {
      if (!state.orders) return null;
      return state.orders.filter(isPendingOrder);
    }),
    unassignedOrders: computed(() => {
      if (!state.orders) return null;
      return state.orders.filter(
        (order) => !isPendingOrder(order) && !isNewOrder(order)
      );
    }),
    pendingOrdersByType: computed(() => {
      if (!getters.pendingOrders.value) return null;
      const ordersByType = ORDER_TYPES.reduce((acc, type) => {
        acc[type.type] = [];
        return acc;
      }, {} as Record<OrderType, IOrder[]>);

      for (const order of getters.pendingOrders.value) {
        ordersByType[order.type].push(order);
      }

      return ordersByType;
    }),
    // Create a set of unseen orders. Not watchable but efficient for checking if an order is unseen
    unseenOrderIdsSet: computed(() => new Set(state.unseenOrderIds)),
    acknowledgedCallIdsSet: computed(() => new Set(state.acknowledgedCallIds)),
    // Calls the user has not acknowledged from the incoming alert yet; drives
    // the call notification sound.
    unacknowledgedCalls: computed(() =>
      (state.calls ?? []).filter(
        (call) => !getters.acknowledgedCallIdsSet.value.has(call.id)
      )
    ),
  };

  const actions = {
    clear() {
      state.orders = null;
      state.snapshotLoadedAt = null;
      state.unseenOrderIds = [];
      state.calls = null;
      state.acknowledgedCallIds = [];
      orderReceivedAt.clear();
    },
    setCalls(calls: ICall[] | null) {
      state.calls = calls;

      // Prune acknowledged ids that no longer exist so the list can't grow
      // unbounded across reloads (null calls = drop all).
      if (state.acknowledgedCallIds.length) {
        const currentIds = new Set((calls ?? []).map((call) => call.id));
        state.acknowledgedCallIds = state.acknowledgedCallIds.filter((id) =>
          currentIds.has(id)
        );
      }
    },
    addCall(call: ICall) {
      if (!state.calls) state.calls = [];
      state.calls.push(call);
    },
    removeCall(callId: number) {
      if (!state.calls) return;
      const index = state.calls.findIndex((c) => c.id === callId);
      if (index !== -1) {
        state.calls.splice(index, 1);
      }
    },
    setOrders(orders: IOrder[] | null) {
      const printedStore = usePrintedStore();
      orderReceivedAt.clear();
      if (orders) {
        const now = Date.now();
        for (const o of orders) {
          printedStore.mergeIntoOrder(o);
          orderReceivedAt.set(o.id, now);
        }
      }

      state.orders = orders;
      state.snapshotLoadedAt = orders ? Date.now() : null;
    },
    updateOrders(newOrders: IOrder[], options?: { snapshotAt?: number }) {
      state.snapshotLoadedAt = Date.now();
      if (state.orders === null) {
        state.orders = [];
      }

      // Map of existing orders by ID for quick lookup
      const existingOrdersMap = new Map(
        state.orders.map((order, index) => [
          order.id,
          {
            order,
            index,
          },
        ])
      );

      // Map of existing order IDs to identify orders that need to be removed (with index)
      const ordersToRemove = new Map(
        state.orders.map((order, index) => [order.id, index])
      );

      // Process each new order
      for (const newOrder of newOrders) {
        const existingOrderData = existingOrdersMap.get(newOrder.id);
        if (existingOrderData) {
          // Update existing order
          actions.updateOrder(newOrder.id, newOrder, existingOrderData.index);
          // Remove from ordersToRemove set since it still exists
          ordersToRemove.delete(newOrder.id);
        } else {
          // Add new order
          actions.addOrder(newOrder, false);
        }
      }

      // Remove orders that no longer exist
      for (const [orderId, index] of ordersToRemove) {
        // Skip orders received after the snapshot request started: they can't
        // be in the snapshot, but their absence doesn't mean deletion.
        const receivedAt = orderReceivedAt.get(orderId);
        if (
          options?.snapshotAt &&
          receivedAt &&
          receivedAt > options.snapshotAt
        ) {
          continue;
        }
        actions.removeOrder(orderId, index);
      }
    },
    addOrder(order: IOrder, checkExisting = true) {
      const printedStore = usePrintedStore();
      printedStore.mergeIntoOrder(order);
      if (!state.orders) state.orders = [];

      if (checkExisting) {
        const existingIndex = state.orders.findIndex((o) => o.id === order.id);

        if (existingIndex !== -1) {
          console.error(
            '[OrdersStore] Order already exists, updating instead',
            order
          );

          actions.updateOrder(order.id, order);
          return;
        }
      }

      state.orders.push(order);
      orderReceivedAt.set(order.id, Date.now());

      if (order.status === OrderStatus.Received) {
        actions.addUnseenOrder(order.id);
      }
    },
    removeOrder(orderId: number, index?: number) {
      if (!state.orders) return;
      if (typeof index === 'undefined' || state.orders[index]?.id !== orderId) {
        index = state.orders.findIndex((o) => o.id === orderId);
      }
      if (index === -1) return;
      state.orders.splice(index, 1);
      orderReceivedAt.delete(orderId);
      actions.removeUnseenOrder(orderId);
    },
    updateOrder(id: number, order: IOrder, index?: number): boolean {
      const printedStore = usePrintedStore();
      printedStore.mergeIntoOrder(order);
      if (!state.orders) return false;

      if (typeof index === 'undefined' || state.orders[index]?.id !== id) {
        index = state.orders.findIndex((o) => o.id === id);
      }

      if (index === -1) {
        if (!isPendingOrder(order) && !isNewOrder(order)) {
          console.log(
            '[OrdersStore] Order not found, but not pending nor new, ignoring',
            order
          );
          return false;
        }

        console.error('[OrdersStore] Order not found, adding instead', order);
        actions.addOrder(order, false);
        return true;
      }

      if (order.status !== OrderStatus.Received) {
        actions.removeUnseenOrder(order.id);
      }

      const oldOrder = state.orders[index];

      // Guard against stale/out-of-order updates
      // - Ignore payloads with older updatedAt than what we already have
      if ('updatedAt' in order && 'updatedAt' in oldOrder) {
        const existingUpdatedAt = oldOrder.updatedAt
          ? new Date(oldOrder.updatedAt as any).getTime()
          : 0;
        const incomingUpdatedAt = order.updatedAt
          ? new Date(order.updatedAt as any).getTime()
          : 0;

        if (existingUpdatedAt && incomingUpdatedAt < existingUpdatedAt) {
          console.warn(
            '[OrdersStore] Ignoring stale order update (older updatedAt)',
            { id, incomingUpdatedAt, existingUpdatedAt }
          );
          return false;
        }
      }

      // TODO: Rework this
      const oldPrintTs = new Map<number, number>(),
        oldPrintCs = new Map<number, number>(),
        oldPrintEs = new Map<number, number>();

      if (oldOrder.orderItems) {
        for (const item of oldOrder.orderItems) {
          oldPrintTs.set(item.index, item.printT || 0);
          oldPrintCs.set(item.index, item.printC || 0);
          oldPrintEs.set(item.index, item.printE || 0);
        }

        if (order.orderItems) {
          for (const item of order.orderItems) {
            item.printT = Math.max(
              item.printT || 0,
              oldPrintTs.get(item.index) || 0
            );
            item.printC = Math.max(
              item.printC || 0,
              oldPrintCs.get(item.index) || 0
            );
            item.printE = Math.max(
              item.printE || 0,
              oldPrintEs.get(item.index) || 0
            );
          }
        }
      }

      // Printed-while-paid marker: preserve only while CONTINUOUSLY Paid.
      // Order-level, so it lives OUTSIDE the orderItems guard above (item-less
      // payloads must not skip it). Trusting the incoming null on a payment
      // state change is what propagates server-side clears (re-pay flow).
      order.printedPaidAt =
        order.printedPaidAt ??
        (order.paymentStatus === OrderPaymentStatus.Paid &&
        oldOrder.paymentStatus === OrderPaymentStatus.Paid
          ? oldOrder.printedPaidAt ?? null
          : null);

      Object.assign(oldOrder, order);
      return true;
    },
    /**
     * Max-merges printed counters broadcast by another device
     * (`v2:orderPrinted`) into the store's copy of the order, so this
     * device's next auto-print evaluation doesn't duplicate the print.
     */
    mergePrintedCounters(payload: {
      orderId: number;
      indexIds: number[];
      printT?: number[];
      printC?: number[];
      printE?: number[];
      printedPaidAt?: string | null;
    }) {
      const order = state.orders?.find((o) => o.id === payload.orderId);
      if (!order) return;

      // Set-only (clears arrive via full v2:orderUpdate payloads). BEFORE the
      // items guard: marker-only broadcasts carry empty indexIds.
      if (payload.printedPaidAt && !order.printedPaidAt) {
        order.printedPaidAt = payload.printedPaidAt;
      }

      if (!order.orderItems) return;

      const itemsByIndex = new Map(
        order.orderItems.map((item) => [item.index, item])
      );

      payload.indexIds.forEach((indexId, i) => {
        const item = itemsByIndex.get(indexId);
        if (!item) return;

        if (payload.printT) {
          item.printT = Math.max(item.printT || 0, payload.printT[i] || 0);
        }
        if (payload.printC) {
          item.printC = Math.max(item.printC || 0, payload.printC[i] || 0);
        }
        if (payload.printE) {
          item.printE = Math.max(item.printE || 0, payload.printE[i] || 0);
        }
      });
    },
    addUnseenOrder(orderId: number) {
      if (!getters.unseenOrderIdsSet.value.has(orderId)) {
        state.unseenOrderIds.push(orderId);
      }
    },
    removeUnseenOrder(orderId: number) {
      if (!getters.unseenOrderIdsSet.value.has(orderId)) return;

      const index = state.unseenOrderIds.indexOf(orderId);
      if (index !== -1) {
        state.unseenOrderIds.splice(index, 1);
      }
    },
    /**
     * Bulk "seen" acknowledgement from the app-bar incoming alert. Stops the
     * order sound (via the watch on unseenOrderIds) and, intentionally, also
     * clears the per-card "unseen" highlights on this device: acknowledging
     * means "I saw these". Orders remain listed until handled.
     */
    clearUnseenOrders() {
      if (state.unseenOrderIds.length) state.unseenOrderIds = [];
    },
    /**
     * Device-local acknowledgement of all current calls: silences the call
     * sound without confirming anything. Later calls re-arm the sound.
     */
    acknowledgeAllCalls() {
      state.acknowledgedCallIds = (state.calls ?? []).map((call) => call.id);
    },
  };

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  };
});
